<template>
  <div>
    <div v-if="hasDiffs" class="diff-icon"
         :class="[
           elevateIconToCardLevel && 'diff-icon-top-level',
         ]"
    >
      <AppTooltip placement="right">
        <AppIcon
          :class="{
            'text-warning': isResolutionNeeded,
            'text-success': isResolved || !differentValuesExist,
          }"
          :style="{ opacity: isResolutionNeeded ? 1 : 0.5 }"
          :name="isResolutionNeeded ? 'warning' : 'check_circle'"
          @click="isModalOpen = isEditable"
        />
        <template #tooltip>
          {{
            isResolutionNeeded
              ? 'Field has different values across records.'
              : 'Field has been reconciled or all values match across records.'
          }}
          <template v-if="!isEditable">
            <br>
            You need to be in edit mode to reconcile fields.
          </template>
        </template>
      </AppTooltip>
    </div>
    <portal to="modal">
      <AppModal
        v-if="isModalOpen"
        @close="isModalOpen = false"
        @clickaway="isModalOpen = false"
      >
        <template #modal-header>
          <h3>Resolve Diff</h3>
        </template>
        <template #modal-body>
          <p>
            This cross listed entity has field values that differ from the
            field values in the current cross listing group.
            You can update the value in this proposal by selecting a value
            from another entity in the cross listing group.
          </p>
          <p v-if="modalDescription">{{ modalDescription }}</p>
          <DynamicFieldDiffReport
            :disabled="false"
            :baseValue="baseValue"
            :currentValue="currentValue"
            :diffs="diffs"
            @resolve="resolveAndClose"
          >
            <template #base="baseValue"><slot name="base" v-bind="baseValue" /></template>
            <template #current><slot name="current" /></template>
            <template #diff="diff"><slot name="diff" v-bind="diff" /></template>
          </DynamicFieldDiffReport>
        </template>
        <template #modal-footer>
          <div class="mt-4 w-100 d-flex">
            <AppButton
              class="ml-auto"
              variant="secondary"
              @click="isModalOpen = false"
            >
              Close
            </AppButton>
          </div>
        </template>
      </AppModal>
    </portal>
  </div>
</template>

<script>
import { isValueEqual } from '@/utils/value'
import { ref, defineComponent, computed, inject } from '@vue/composition-api'
import DynamicFieldDiffReport from './DynamicFieldDiffReport.vue'
import { areTermsEqual } from '@shared/utils/terms'

export default defineComponent({
  components: {
    DynamicFieldDiffReport,
  },
  props: {
    isEditable: {
      type: Boolean,
      default: false,
    },
    diffs: {
      type: Array,
      required: true,
    },
    currentValue: {
      type: [ Array, Object, String, Number, Boolean ],
      required: false,
    },
    baseValue: {
      type: Object,
      default: () => ({}),
    },
    elevateIconToCardLevel: {
      type: Boolean,
      default: false,
    },
  },
  setup (props, { emit }) {
    const isModalOpen = ref(false)
    const isResolved = ref(false)
    const dynamicFieldQuestionPath = inject('dynamic_field_question_path', [])
    const modalDescription = inject('dynamic_form_diff_modal_description', '')
    const dynamicFieldQuestionPathString = dynamicFieldQuestionPath.join('.')
    const differentValuesExist = computed(() => {
      const diffsValues = props.diffs.map(diff => diff.value)
      const combinedValues = [
        ...diffsValues,
        ...(props.baseValue.hasOwnProperty('value') ? [ props.baseValue.value ] : []),
      ]
      return combinedValues.some(value => !isFieldEqual(dynamicFieldQuestionPathString, value, props.currentValue))
    })
    const isResolutionNeeded = computed(() =>
      !isResolved.value && differentValuesExist.value
    )

    const hasDiffs = computed(() => props.diffs.length || props.baseValue?.property)

    function isFieldEqual (path, a, b) {
      switch (path) {
        case 'startTerm':
        case 'endTerm':
          return isValueEqual(a, b) || areTermsEqual(a, b)
        default:
          return isValueEqual(a, b)
      }
    }

    function resolveAndClose (diff) {
      if (diff !== undefined) emit('resolve', diff.value)
      isResolved.value = true
      isModalOpen.value = false
    }

    return {
      isModalOpen,
      isResolved,
      resolveAndClose,
      differentValuesExist,
      isResolutionNeeded,
      hasDiffs,
      modalDescription,
    }
  },
})
</script>

<style scoped>
.diff-icon {
  position: absolute;
  left: -27px;
  top: 0px;
  cursor: pointer;
  background: white;
  border-radius: 100%;
  padding: 3px;
  line-height: 10px;
  display: flex;
  align-content: center;
  align-items: center;
}

.diff-icon-top-level {
  top: -43px;
}
</style>
